import React, { useState, useEffect } from 'react'
import { Button } from '../Button'
import { DataTable, Column, TableAction } from '../common/DataTable'
import { api } from '../../services/api'
import { PackageForm, PackageFormData } from './PackageForm'

interface Package {
  id: string
  name: string
  slug: string
  description: string
  short_description: string
  package_type: string
  billing_type: string
  setup_fee?: number
  monthly_price?: number
  yearly_price?: number
  one_time_price?: number
  features: Array<{
    title: string
    description: string
    included: boolean
    highlighted: boolean
  }>
  technical_specs: {
    cpu_cores?: number
    ram_gb?: number
    storage_gb?: number
    bandwidth_gb?: number
    ssl_included?: boolean
    backup_included?: boolean
    monitoring_included?: boolean
  }
  services_included?: Array<{
    name: string
    image: string
    ports?: number[]
    environment?: Record<string, string>
    volumes?: string[]
  }>
  includes_google_workspace?: boolean
  google_workspace_tier?: string
  is_featured: boolean
  is_popular: boolean
  is_available: boolean
  is_active: boolean
  badge_text?: string
  image_url?: string
  created_at: string
}

interface PackageListResponse {
  packages: Package[]
  total: number
  page: number
  size: number
  has_next: boolean
  has_previous: boolean
}

export function PackageManagement() {
  const [packages, setPackages] = useState<Package[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingPackage, setEditingPackage] = useState<Package | null>(null)
  const [formLoading, setFormLoading] = useState(false)

  const loadPackages = async () => {
    try {
      setLoading(true)
      const response = await api.get<PackageListResponse>(`/api/packages/?page=${page}&size=10`)
      setPackages(response.packages)
      setTotal(response.total)
    } catch (error) {
      console.error('Failed to load packages:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPackages()
  }, [page])

  const togglePackageActive = async (packageId: string) => {
    try {
      await api.post(`/api/packages/${packageId}/toggle-active`)
      await loadPackages()
    } catch (error) {
      console.error('Failed to toggle package status:', error)
    }
  }

  const togglePackageFeatured = async (packageId: string) => {
    try {
      await api.post(`/api/packages/${packageId}/toggle-featured`)
      await loadPackages()
    } catch (error) {
      console.error('Failed to toggle featured status:', error)
    }
  }

  const seedPackages = async () => {
    try {
      await api.post('/api/packages/seed')
      await loadPackages()
    } catch (error) {
      console.error('Failed to seed packages:', error)
    }
  }

  const handleCreatePackage = async (formData: PackageFormData) => {
    try {
      setFormLoading(true)
      await api.post('/api/packages/', formData)
      setShowCreateForm(false)
      await loadPackages()
    } catch (error) {
      console.error('Failed to create package:', error)
      throw error
    } finally {
      setFormLoading(false)
    }
  }

  const handleEditPackage = async (formData: PackageFormData) => {
    if (!editingPackage) return

    try {
      setFormLoading(true)
      await api.put(`/api/packages/${editingPackage.id}`, formData)
      setShowEditForm(false)
      setEditingPackage(null)
      await loadPackages()
    } catch (error) {
      console.error('Failed to update package:', error)
      throw error
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeletePackage = async (packageId: string) => {
    if (!confirm('Are you sure you want to delete this package?')) return

    try {
      await api.delete(`/api/packages/${packageId}`)
      await loadPackages()
    } catch (error) {
      console.error('Failed to delete package:', error)
    }
  }

  const openEditForm = async (pkg: Package) => {
    try {
      // Fetch full package details for editing
      const response = await api.get(`/api/packages/${pkg.id}`)
      setEditingPackage(response)
      setShowEditForm(true)
    } catch (error) {
      console.error('Failed to load package details:', error)
    }
  }

  const closeCreateForm = () => {
    setShowCreateForm(false)
  }

  const closeEditForm = () => {
    setShowEditForm(false)
    setEditingPackage(null)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const columns: Column<Package>[] = [
    {
      key: 'name',
      header: 'Package',
      sortable: true,
      editable: true,
      onEdit: async (pkg, newValue) => {
        try {
          await api.patch(`/api/packages/${pkg.id}`, { name: newValue })
          await loadPackages()
        } catch (error) {
          console.error('Failed to update package name:', error)
        }
      },
      render: (pkg) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{pkg.name}</div>
          <div className="text-sm text-gray-500">{pkg.slug}</div>
        </div>
      )
    },
    {
      key: 'package_type',
      header: 'Type',
      sortable: true,
      render: (pkg) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {pkg.package_type.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'monthly_price',
      header: 'Price',
      sortable: true,
      editable: true,
      onEdit: async (pkg, newValue) => {
        try {
          await api.patch(`/api/packages/${pkg.id}`, { monthly_price: parseFloat(newValue) })
          await loadPackages()
        } catch (error) {
          console.error('Failed to update package price:', error)
        }
      },
      render: (pkg) => (
        <div className="text-sm text-gray-900">
          {pkg.monthly_price && `$${Number(pkg.monthly_price).toFixed(2)}/month`}
          {pkg.yearly_price && !pkg.monthly_price && `$${Number(pkg.yearly_price).toFixed(2)}/year`}
          {pkg.one_time_price && !pkg.monthly_price && !pkg.yearly_price && `$${Number(pkg.one_time_price).toFixed(2)} one-time`}
          {!pkg.monthly_price && !pkg.yearly_price && !pkg.one_time_price && 'Custom pricing'}
        </div>
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (pkg) => (
        <div className="space-y-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            pkg.is_active
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {pkg.is_active ? 'Active' : 'Inactive'}
          </span>
          {pkg.is_featured && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Featured
            </span>
          )}
          {pkg.is_popular && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Popular
            </span>
          )}
        </div>
      )
    }
  ]

  const actions: TableAction<Package>[] = [
    {
      label: 'Edit',
      variant: 'outline',
      onClick: openEditForm
    },
    {
      label: pkg => pkg.is_active ? 'Deactivate' : 'Activate',
      variant: 'outline',
      onClick: (pkg) => togglePackageActive(pkg.id)
    },
    {
      label: pkg => pkg.is_featured ? 'Unfeature' : 'Feature',
      variant: 'outline',
      onClick: (pkg) => togglePackageFeatured(pkg.id)
    },
    {
      label: 'Delete',
      variant: 'outline',
      onClick: (pkg) => handleDeletePackage(pkg.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ]

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Package Management</h2>
        <div className="space-x-3">
          <Button
            variant="outline"
            onClick={seedPackages}
          >
            Seed Sample Packages
          </Button>
          <Button onClick={() => setShowCreateForm(true)}>
            Create Package
          </Button>
        </div>
      </div>

      {/* Enhanced DataTable */}
      <DataTable
        data={packages}
        columns={columns}
        actions={actions}
        loading={loading}
        title="Packages"
        searchable={true}
        sortable={true}
        emptyMessage="No packages found. Create your first package to get started."
      />

      {/* Create Package Form */}
      {showCreateForm && (
        <PackageForm
          onSubmit={handleCreatePackage}
          onCancel={closeCreateForm}
          loading={formLoading}
        />
      )}

      {/* Edit Package Form */}
      {showEditForm && editingPackage && (
        <PackageForm
          initialData={editingPackage}
          onSubmit={handleEditPackage}
          onCancel={closeEditForm}
          loading={formLoading}
        />
      )}
    </div>
  )
}